import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus } = FiIcons;

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    { 
      question: "How fast is your turnaround?", 
      answer: "Turnaround depends on the plan you choose,ranging from same-day delivery to 48 hours." 
    },
    { 
      question: "Who is Living Media for?", 
      answer: "We work with creators,brands,agencies,and businesses who want consistent,high-quality video without hiring in-house." 
    },
    { 
      question: "Is there a contract or minimum commitment?", 
      answer: "No long-term contracts. You can pause or cancel anytime." 
    },
    { 
      question: "What happens after the 7-day free trial?", 
      answer: "If you like the experience,we can continue on a subscription. If not,you keep the edits — no obligation." 
    },
    { 
      question: "How do revisions work?", 
      answer: "All plans include unlimited revisions during your active subscription." 
    }
  ];

  // Inject JSON-LD Schema
  useEffect(() => {
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []); // Static content, run once on mount

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-white py-16 md:py-32 section-curve-up">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-20"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 md:w-10 h-[2px] bg-[#BEF264]" />
            <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.4em] text-black/40">Clarification</span>
          </div>
          <h2 className="text-4xl md:text-7xl font-black text-black tracking-tighter leading-none uppercase">
            Frequently Asked <span className="text-black/20 italic">Questions</span>
          </h2>
        </motion.div>

        <div className="max-w-4xl">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-black/5 last:border-0 relative">
              <button 
                onClick={() => toggleFAQ(index)}
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
                className="w-full py-6 md:py-8 flex items-start md:items-center justify-between text-left group cursor-pointer bg-transparent"
              >
                <span className="text-lg md:text-2xl font-black text-black uppercase tracking-tight group-hover:text-[#BEF264] transition-colors duration-300 pr-8 leading-tight">
                  {faq.question}
                </span>
                <div className="relative w-6 h-6 flex items-center justify-center shrink-0 mt-1 md:mt-0">
                  <motion.div 
                    initial={false}
                    animate={{ rotate: activeIndex === index ? 180 : 0, opacity: activeIndex === index ? 0 : 1 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <SafeIcon icon={FiPlus} className="w-5 h-5 text-[#BEF264]" />
                  </motion.div>
                  <motion.div 
                    initial={false}
                    animate={{ rotate: activeIndex === index ? 0 : -90, opacity: activeIndex === index ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <SafeIcon icon={FiMinus} className="w-5 h-5 text-[#BEF264]" />
                  </motion.div>
                </div>
              </button>
              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div 
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-8 text-black/60 font-medium text-sm md:text-lg leading-relaxed max-w-2xl tracking-tight">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;